"use client";

import PlusIcon from "@/assets/icons/add.svg";
import CloseIcon from "@/assets/icons/close.svg";
import MinusIcon from "@/assets/icons/subtract.svg";
import NoImage from "@/assets/images/no_image.svg";
import CldImage from "@/components/ui/cldImg/CldImage";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { getLangFromLocale } from "@/utils/common";
import { isVideoMedia } from "@/apiUtils/productUtils";
import { PDP_ZOOM_MAX_LEVEL, PDP_ZOOM_MOUNT_RANGE, PDP_ZOOM_STEP, saleorMediaType } from "@/apiUtils/constants";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import Modal from "../../ui/modal_new/Modal";
import { ImageType } from "../types";
import { TranslationData } from "@/common/lib/types";
import { VideoThumbnail, VideoSlide, useVideoPlayer } from "@/components/pdp/VideoPlayOverlay";

export interface IProps {
  altText: string;
  isOpen: boolean;
  onClose: (status: boolean) => void;
  images: ImageType[];
  dir: "ltr" | "rtl";
  selected: number;
  translations?: TranslationData;
}

interface AvailableTabs {
  title: string;
  value: string;
}

interface MediaIndex {
  image: number;
  video: number;
}
const ProductImageViewer = ({ isOpen, onClose, images, dir, altText, selected, translations }: IProps) => {
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);

  // Video playback for modal
  const { setVideoRef, playVideo, pauseAllVideos, toggleVideo, playingIdx } = useVideoPlayer();

  // Separate images and videos
  const [imageMedias, videoMedias] = useMemo(() => {
    const imageMediasIn = images?.filter((media) => !isVideoMedia({ url: media.url, type: media.mediaType })) || [];
    const videoMediasIn = images?.filter((media) => isVideoMedia({ url: media.url, type: media.mediaType })) || [];
    return [imageMediasIn, videoMediasIn];
  }, [images]);

  // Determine available tabs
  const availableTabs = useMemo(() => {
    const tabs: AvailableTabs[] = [];
    const pdpTranslations = translations?.pdp || {};
    if (imageMedias.length > 0) {
      tabs.push({ 
        title: pdpTranslations?.pdp_media_overlay_tab_image || "Images", 
        value: saleorMediaType.MEDIA_TYPE_IMAGE 
      });
    }
    if (videoMedias.length > 0) {
      tabs.push({ 
        title: pdpTranslations?.pdp_media_overlay_tab_video || "Videos", 
        value: saleorMediaType.MEDIA_TYPE_VIDEO 
      });
    }
    return tabs;
  }, [imageMedias.length, videoMedias.length, translations]);

  // Determine initial active tab based on selected media
  const getInitialTab = useCallback(() => {
    const selectedMedia = images[selected];
    if (selectedMedia && isVideoMedia({ url: selectedMedia.url, type: selectedMedia.mediaType })) {
      return saleorMediaType.MEDIA_TYPE_VIDEO;
    }
    return saleorMediaType.MEDIA_TYPE_IMAGE;
  }, [images, selected]);

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [activeMediaIndex, setActiveMediaIndex] = useState<MediaIndex>({ image: 0, video: 0 });

  // Get current media list based on active tab
  const currentMediaList = useMemo(() => {
    return activeTab === saleorMediaType.MEDIA_TYPE_IMAGE ? imageMedias : videoMedias;
  }, [activeTab, imageMedias, videoMedias]);

  const zoomRefs = useRef<Array<ReactZoomPanPinchRef | null>>([]);
  const isZoomedRef = useRef(false);
  const [isZoomed, setIsZoomedState] = useState(false);

  const setIsZoomed = useCallback((zoomed: boolean) => {
    isZoomedRef.current = zoomed;
    setIsZoomedState(zoomed);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: dir,
    containScroll: "trimSnaps",
    loop: true,
    startIndex: activeTab === saleorMediaType.MEDIA_TYPE_IMAGE ? activeMediaIndex.image : activeMediaIndex.video,
    // Swiping must not fight with panning a zoomed image
    watchDrag: () => !isZoomedRef.current,
  });
  const [selectedThb, setSelectedThb] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  // Only slides near the active one load the full-resolution zoom image
  const isNearActiveSlide = (idx: number) => {
    const total = currentMediaList?.length ?? 0;
    const distance = Math.abs(idx - selectedThb);
    return Math.min(distance, total - distance) <= PDP_ZOOM_MOUNT_RANGE;
  };

  const resetAllZoom = useCallback(() => {
    zoomRefs.current.forEach((ref) => ref?.resetTransform(0));
    setIsZoomed(false);
  }, [setIsZoomed]);

  // Update active media index when selected changes
  useEffect(() => {
    const selectedMedia = images[selected];
    if (selectedMedia) {
      const isVideo = isVideoMedia({ url: selectedMedia.url, type: selectedMedia.mediaType });
      if (isVideo) {
        setActiveTab(saleorMediaType.MEDIA_TYPE_VIDEO);
        const videoIndex = videoMedias.findIndex(v => v.url === selectedMedia.url);
        setActiveMediaIndex({ image: 0, video: videoIndex >= 0 ? videoIndex : 0 });
      } else {
        setActiveTab(saleorMediaType.MEDIA_TYPE_IMAGE);
        const imageIndex = imageMedias.findIndex(i => i.url === selectedMedia.url);
        setActiveMediaIndex({ image: imageIndex >= 0 ? imageIndex : 0, video: 0 });
      }
    }
  }, [selected, images, imageMedias, videoMedias]);

  useEffect(() => {
    zoomRefs.current = [];
    setIsZoomed(false);
  }, [currentMediaList, setIsZoomed]);

  // Update embla carousel when tab changes + autoplay video if on video tab
  useEffect(() => {
    if (emblaApi) {
      const startIndex = activeTab === saleorMediaType.MEDIA_TYPE_IMAGE ? activeMediaIndex.image : activeMediaIndex.video;
      emblaApi.scrollTo(startIndex, true);
      setSelectedThb(startIndex);
      // Autoplay video when navigating to video tab
      if (activeTab === saleorMediaType.MEDIA_TYPE_VIDEO) {
        setTimeout(() => playVideo(startIndex), 100);
      } else {
        pauseAllVideos();
      }
    }
  }, [activeTab, emblaApi, activeMediaIndex]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const currentIndex = emblaApi.selectedScrollSnap();
    setSelectedThb(currentIndex);
    resetAllZoom();

    // Update active media index based on current tab
    if (activeTab === saleorMediaType.MEDIA_TYPE_IMAGE) {
      setActiveMediaIndex(prev => ({ ...prev, image: currentIndex }));
    } else {
      setActiveMediaIndex(prev => ({ ...prev, video: currentIndex }));
    }
  }, [emblaApi, activeTab, resetAllZoom]);

  const thbHandleClick = useCallback(
    (idx: number) => {
      setSelectedThb(idx);
      if (emblaApi) emblaApi.scrollTo(idx);
      const item = currentMediaList[idx];
      if (item && isVideoMedia({ url: item.url, type: item.mediaType })) {
        setTimeout(() => playVideo(idx), 50);
      } else {
        pauseAllVideos();
      }
    },
    [emblaApi, currentMediaList, playVideo, pauseAllVideos],
  );

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
  });

  return (
    <div>
      <div className="flex w-full items-center justify-between border-b px-5 pt-5">
        <div className="flex flex-1 items-center justify-center gap-8">
          {availableTabs.map(({ title, value }) => (
            <div
              key={value}
              role="button"
              className={cn(
                "cursor-pointer pb-5 text-sm font-semibold transition-colors",
                activeTab === value
                  ? "border-b-2 border-black text-black"
                  : "border-b-2 border-transparent text-gray-500 hover:text-gray-700"
              )}
              onClick={() => toggleTab(value)}
            >
              {title}
            </div>
          ))}
        </div>
        <Image
          role="button"
          alt="close"
          src={CloseIcon}
          width={20}
          height={20}
          className="h-3 w-3 cursor-pointer"
          onClick={() => onClose(false)}
        />
      </div>
      <div className="w-full py-5">
        <div
          className={cn(
            "mx-auto overflow-hidden",
            activeTab === saleorMediaType.MEDIA_TYPE_VIDEO
              ? "max-h-[80vh] w-full max-w-[32.125rem]"
              : "h-[32rem] max-w-[32.125rem] md:h-[36.063rem]",
          )}
          ref={emblaRef}
        >
          <div className={cn("flex", activeTab === saleorMediaType.MEDIA_TYPE_VIDEO ? "h-auto" : "h-full")}>
            {currentMediaList?.map((image, idx) => {
              const isVideo = isVideoMedia({ url: image.url, type: image.mediaType });
              return (
                <div
                  key={idx}
                  className="no-scrollbar relative flex min-w-0 items-center overflow-hidden bg-tertiary-5"
                  style={{ flex: "0 0 100%" }}
                >
                  {isVideo ? (
                    <VideoSlide
                      src={image ? (getCloudinaryURL(image.url) || "") : ""}
                      isPlaying={playingIdx === idx}
                      onToggle={(e) => toggleVideo(idx, e)}
                      videoRef={setVideoRef(idx)}
                      objectFit="contain"
                      onLoaded={() => {
                        // Autoplay this video when it loads and is the active slide
                        if (activeTab === saleorMediaType.MEDIA_TYPE_VIDEO && selectedThb === idx) {
                          playVideo(idx);
                        }
                      }}
                    />
                  ) : !isNearActiveSlide(idx) ? null : (
                    <TransformWrapper
                      ref={(ref) => {
                        zoomRefs.current[idx] = ref;
                      }}
                      initialScale={1}
                      minScale={1}
                      maxScale={PDP_ZOOM_MAX_LEVEL}
                      wheel={{ step: 0.0025 }}
                      pinch={{ step: 5 }}
                      doubleClick={{ mode: "toggle" }}
                      // Panning at scale 1 swallows touch swipes meant for the carousel
                      panning={{ disabled: !(isZoomed && idx === selectedThb) }}
                      centerZoomedOut={true}
                      centerOnInit={true}
                      limitToBounds={true}
                      onTransform={(ref) => {
                        const zoomed = (ref?.state?.scale ?? 1) > 1.05;
                        if (idx === selectedThb && zoomed !== isZoomedRef.current) {
                          setIsZoomed(zoomed);
                        }
                      }}
                    >
                      {({ zoomIn, zoomOut }) => (
                        <>
                          <TransformComponent
                            wrapperStyle={{ width: "100%", height: "100%" }}
                            contentStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CldImage
                              src={image ? getCloudinaryURL(image.url) : NoImage}
                              alt={altText}
                              width={1600}
                              height={1920}
                              loading="eager"
                              draggable={false}
                              className="invert-[2%]"
                              style={{
                                userSelect: "none",
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                            />
                          </TransformComponent>
                          <div className="absolute bottom-10 right-10 hidden select-none flex-col gap-3 md:flex">
                            <button
                              type="button"
                              className="min-w-0 border-0 bg-white p-2 active:bg-tertiary-5"
                              title="Zoom In"
                              aria-label="Zoom in"
                              onClick={() => zoomIn(PDP_ZOOM_STEP)}
                            >
                              <Image src={PlusIcon} alt="" width={20} height={20} className="w-3" />
                            </button>
                            <button
                              type="button"
                              className="min-w-0 border-0 bg-white px-2 py-[.84rem] active:bg-tertiary-5"
                              title="Zoom Out"
                              aria-label="Zoom out"
                              onClick={() => zoomOut(PDP_ZOOM_STEP)}
                            >
                              <Image src={MinusIcon} alt="" width={20} height={20} className="w-3" />
                            </button>
                          </div>
                        </>
                      )}
                    </TransformWrapper>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Scrollbar for mobile */}
        <div className={cn("flex transform items-center duration-300 ease-in-out md:hidden")}>
          {scrollSnaps.map((_, i) => (
            <div
              key={i}
              onClick={() => {
                onDotButtonClick(i);
              }}
              className={cn(
                "flex-1",
                selectedThb === i ? "h-[3px] bg-black" : "h-[1px] bg-[rgba(212,212,212,1)]",
              )}
            ></div>
          ))}
        </div>

        <div className="no-scrollbar mt-8 hidden w-full items-center justify-center overflow-x-scroll md:flex">
          <div className="flex w-full min-w-max items-center justify-center gap-4 overflow-hidden">
            {currentMediaList?.map((item, idx) => {
              const isVideo = isVideoMedia({ url: item.url, type: item.mediaType });
              return (
                <div
                  key={idx}
                  role="button"
                  className={cn(
                    "flex h-[8.5rem] w-[7rem] items-center bg-tertiary-5",
                    idx === selectedThb ? "border" : "",
                  )}
                  onClick={() => thbHandleClick(idx)}
                >
                  {isVideo ? (
                    <VideoThumbnail src={getCloudinaryURL(item.url) || item.url} />
                  ) : (
                    <CldImage
                      src={item ? getCloudinaryURL(item.url) : NoImage.src}
                      alt={altText}
                      width={98}
                      height={115}
                      className="w-[100vw] invert-[2%]"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductImageViewerModal = ({ isOpen, onClose, images, dir, altText, selected, translations }: IProps) => {
  return (
    <div>
      <Modal
        isOpen={isOpen}
        onClose={() => onClose(false)}
        slots={{
          close: { visible: false },
          backdrop: { className: "backdrop-blur-0" },
          container: { className: "w-full md:w-[90%] lg:w-[70%] p-4" },
        }}
        children={
          <ProductImageViewer
            selected={selected}
            images={images}
            isOpen={isOpen}
            onClose={onClose}
            dir={dir}
            altText={altText}
            translations={translations}
          />
        }
      />
    </div>
  );
};

export default ProductImageViewerModal;
